import { useLingui } from '@lingui/react/macro';
import { Stack, Typography } from '@mui/material';
import { type ReactNode, useMemo } from 'react';
import { Button } from 'ui-kit';
import { erc20Abi, formatUnits, getAddress } from 'viem';
import { useAccount, useBalance, useReadContract } from 'wagmi';
import { EmptyDash } from '@/components/empty-dash';
import { usePositionStore } from '@/feature/terminal_tables/position/PositionStore.ts';
import { sumOpenPositionsEquity } from '@/feature/terminal_tables/position/positionUtils.ts';
import { PositionStatus } from '@/feature/terminal_tables/position/types.ts';
import { useTokenFormatter } from '@/hooks/useTokenFormatter.ts';
import { bigIntToBig, bigToBigInt, stringToBig } from '@/utils/bigUtils.ts';
import { formatTokenAmountDisplay } from '@/utils/tokenNumberUtils';
import useAppStore, { useAccountBalance, useSelectedMarket, useTotalAccountBalance } from '../../../store/rootStore';
import { getAccountFeeTierLabel } from './fees_panel.tsx';

interface BalanceRowProps {
  label: string;
  value: ReactNode;
}

function BalanceRow({ label, value }: BalanceRowProps) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5} sx={{ py: 0.625 }}>
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 500 }} className="numeric" component="div">
        {value}
      </Typography>
    </Stack>
  );
}

interface AccountBalanceSummaryProps {
  onDeposit: () => void;
  onWithdraw: () => void;
  onFees?: () => void;
  canWithdraw: boolean;
}

export function AccountBalanceSummary({ onDeposit, onWithdraw, onFees, canWithdraw }: AccountBalanceSummaryProps) {
  const { t } = useLingui();
  const { address } = useAccount();
  const token = useAppStore((state) => state.exchange?.token);
  const chainId = useAppStore((state) => state.config?.chain.id);
  const isDelegated = useAppStore((state) => state.isDelegated);
  const activeDelegatedAddress = useAppStore((state) => state.activeDelegatedAddress);
  const feeTiersEnabled = useAppStore((state) => state.config?.features.feeTiersEnabled);
  const accountFeeTier = useAppStore((state) => state.account?.ft);
  const selectedMarket = useSelectedMarket();
  const showFees = Boolean(feeTiersEnabled && selectedMarket && onFees);
  const feeTierLabel = getAccountFeeTierLabel(accountFeeTier);

  const tokenAddress = token?.address ? getAddress(token.address) : undefined;
  const delegatedAddress = isDelegated && activeDelegatedAddress ? getAddress(activeDelegatedAddress) : undefined;
  const walletBalanceAddress = isDelegated ? delegatedAddress : address;

  const availableBalance = useAccountBalance();
  const totalBalance = useTotalAccountBalance();
  const positions = usePositionStore((state) => state.positions);
  const formatToken = useTokenFormatter(token);

  const { data: nativeBalance, isPending: isNativePending } = useBalance({
    address: walletBalanceAddress,
    chainId,
  });

  const { data: collateralBalance, isPending: isCollateralPending } = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: walletBalanceAddress ? [walletBalanceAddress] : undefined,
    chainId,
    query: { enabled: !!tokenAddress && !!walletBalanceAddress },
  });

  const openPositions = useMemo(() => positions.filter((p) => p.status === PositionStatus.open), [positions]);

  const accountValue = useMemo(() => {
    if (!token) {
      return stringToBig(0);
    }
    const equity = sumOpenPositionsEquity(openPositions);
    return bigIntToBig(totalBalance, token.decimals).plus(equity);
  }, [openPositions, token, totalBalance]);

  if (!token) {
    return null;
  }

  const formatUsd = (value: bigint) => formatToken(value, { trailingZeroDisplay: 'auto', minDecimals: 2, maxDecimals: 2 });

  // `account.b` missing → exchange balances still loading (don't flash $0.00).
  const accountBalancesReady = availableBalance !== undefined;
  const accountValueDisplay = accountBalancesReady ? `$${formatUsd(bigToBigInt(accountValue, token.decimals))}` : <EmptyDash />;
  const availableDisplay = accountBalancesReady ? `$${formatUsd(availableBalance)}` : <EmptyDash />;
  const walletCollateralDisplay =
    !isCollateralPending && collateralBalance !== undefined ? (
      `${formatTokenAmountDisplay(collateralBalance, token, { minDecimals: 2, maxDecimals: 2 })} ${token.symbol}`
    ) : (
      <EmptyDash />
    );
  const nativeSymbol = nativeBalance?.symbol ?? 'MON';
  const gasDisplay =
    !isNativePending && nativeBalance ? (
      `${parseFloat(formatUnits(nativeBalance.value, nativeBalance.decimals)).toFixed(4)} ${nativeSymbol}`
    ) : (
      <EmptyDash />
    );

  const depositLabel = t({ comment: 'Deposit action in account menu', message: 'Deposit' });
  const withdrawLabel = t({ comment: 'Withdraw action in account menu', message: 'Withdraw' });
  const feesLabel = feeTierLabel
    ? t({ comment: 'Open fee tiers panel in account menu with user tier', message: `Fees Tier ${feeTierLabel}` })
    : t({ comment: 'Open fee tiers panel in account menu', message: 'Fees' });

  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography sx={{ fontSize: 11, fontWeight: 500, color: 'text.secondary', letterSpacing: 0.04 }}>
            {t({ comment: 'Account value label in account menu', message: 'Account value' })}
          </Typography>
          {showFees ? (
            <Typography
              component="button"
              type="button"
              onClick={onFees}
              sx={{
                all: 'unset',
                cursor: 'pointer',
                flexShrink: 0,
                fontSize: 11,
                fontWeight: 500,
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              {feesLabel}
            </Typography>
          ) : null}
        </Stack>
        <Typography
          sx={{
            display: 'block',
            width: '100%',
            fontSize: 28,
            fontWeight: 600,
            lineHeight: 1.1,
            letterSpacing: -0.03,
            fontVariantNumeric: 'tabular-nums',
            overflowWrap: 'anywhere',
          }}
          className="numeric"
          component="div"
        >
          {accountValueDisplay}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1}>
        <Button variant="contained" size="medium" fullWidth onClick={onDeposit}>
          {depositLabel}
        </Button>
        {canWithdraw ? (
          <Button variant="outlined" size="medium" fullWidth onClick={onWithdraw}>
            {withdrawLabel}
          </Button>
        ) : null}
      </Stack>

      <Stack
        spacing={0}
        sx={{
          '& > *:not(:last-child)': {
            borderBottom: '1px solid',
            borderColor: 'elevation.outlined',
          },
        }}
      >
        <BalanceRow label={t({ comment: 'Available balance row', message: 'Available' })} value={availableDisplay} />
        <BalanceRow
          label={
            isDelegated
              ? t({ comment: 'Wallet collateral row', message: 'Wallet' })
              : t({ comment: 'On-chain collateral row', message: 'On-chain' })
          }
          value={walletCollateralDisplay}
        />
        <BalanceRow label={t({ comment: 'Native gas balance row', message: 'Gas' })} value={gasDisplay} />
      </Stack>
    </Stack>
  );
}
